<template>
  <!-- Einziger schwebender Knopf unten rechts: öffnet den KARS-KI Assistenten,
       in dem auch „Fehler melden“ und die Supportanfrage liegen. Ersetzt den
       früheren Knopf „Fehler melden“ an derselben Stelle (gleiche Navy-Pille,
       --app-fab-lift für feste Fußleisten). Strg/⌘ + J öffnet ebenfalls. -->
  <button
    aria-controls="kars-ai-panel"
    :aria-expanded="store.isOpen"
    :aria-label="store.isOpen ? $gt('KARS-KI schließen') : $gt('KARS-KI öffnen')"
    class="ai-launcher"
    :class="{ 'is-open': store.isOpen }"
    data-testid="ai-launcher"
    type="button"
    @click="store.toggle()"
  >
    <span aria-hidden="true" class="ai-launcher__brand">
      <img alt="" class="ai-launcher__logo" :src="logo">
      <span class="ai-launcher__ki">KI</span>
    </span>
    <span class="ai-launcher__label">{{ store.isOpen ? $gt('Schließen') : $gt('Hilfe & Fehler melden') }}</span>
    <span v-if="hasUnseen && !store.isOpen" class="ai-launcher__badge" :title="$gt('Neue Hinweise für Sie')">
      <span aria-hidden="true" class="ai-launcher__ping" />
      {{ store.unseenInsights.length }}
    </span>
  </button>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted } from 'vue'
  import logo from '@/assets/images/logo_white.png'
  import { useAssistantStore } from '@/stores/assistantStore'
  import './assistant.css'

  const store = useAssistantStore()
  const hasUnseen = computed(() => store.unseenInsights.length > 0)

  function onKeydown (event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'j') {
      event.preventDefault()
      store.toggle()
    }
  }

  onMounted(() => {
    window.addEventListener('keydown', onKeydown)
    // Kontext (Hinweise) früh laden, damit das Badge erscheinen kann.
    void store.ensureContext()
  })

  onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
</script>

<style scoped>
  .ai-launcher {
    position: fixed;
    right: 24px;
    /* --app-fab-lift: Höhe einer fest stehenden Fußleiste (Fragebogen mobil). */
    bottom: calc(24px + var(--app-fab-lift, 0px));
    z-index: var(--ai-z);
    display: inline-flex;
    align-items: center;
    gap: 10px;
    height: 44px;
    padding: 0 16px 0 12px;
    border: 0;
    border-radius: 999px;
    background: var(--ai-navy);
    color: #fff;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(26, 27, 46, 0.28);
    transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
  }

  .ai-launcher:hover {
    transform: scale(1.04);
    box-shadow: 0 6px 20px rgba(26, 27, 46, 0.35);
  }

  .ai-launcher:active {
    transform: scale(0.98);
  }

  .ai-launcher.is-open {
    background: var(--ai-navy-soft);
  }

  .ai-launcher:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 118, 229, 0.35), 0 4px 14px rgba(26, 27, 46, 0.28);
  }

  .ai-launcher__brand {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .ai-launcher__logo {
    display: block;
    width: auto;
    height: 13px;
  }

  .ai-launcher__ki {
    padding: 1px 5px;
    border-radius: 4px;
    background: var(--ai-blue);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    line-height: 14px;
  }

  .ai-launcher__label {
    white-space: nowrap;
  }

  .ai-launcher__badge {
    position: absolute;
    top: -6px;
    right: -4px;
    display: grid;
    place-items: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border: 2px solid #fff;
    border-radius: 10px;
    background: var(--ai-danger);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    animation: ai-pop 360ms var(--ai-ease) both;
  }

  .ai-launcher__ping {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: var(--ai-danger);
    animation: ai-ping 1.6s cubic-bezier(0, 0, 0.2, 1) infinite;
    pointer-events: none;
  }

  @media (max-width: 599px) {
    .ai-launcher {
      right: 16px;
      bottom: calc(16px + var(--app-fab-lift, 0px));
      padding: 0 14px 0 12px;
    }

    .ai-launcher.is-open {
      display: none;
    }

    .ai-launcher__label {
      display: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .ai-launcher__badge,
    .ai-launcher__ping {
      animation: none !important;
    }

    .ai-launcher__ping {
      display: none;
    }
  }
</style>
